/**
 * ProjectBurnSpend Component
 *
 * A burn report per project: contracted budget from the position export against
 * actual client spend drawn from platform payout records, bucketed by month.
 *
 * Projects that carry a budget but have no billed time in the platform are
 * still listed, at zero spend, with a marker explaining the gap so the totals
 * here reconcile with the Contract Progression tab.
 *
 * URL Parameters:
 * - ?search=text - Filter by project, position, or consultant name
 * - ?scope=active|all|completed - Which projects to list
 * - ?health=healthy|warning|over|unknown - Filter by budget health
 * - ?sort_by=budget|spent|consumed|remaining|name - Sorting
 */

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input, Select, Tooltip } from "antd";
import { useAPI } from "../../config/API";

const { Option } = Select;
const { Search } = Input;

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                          */
/* -------------------------------------------------------------------------- */

const currency = (value, decimals = 0) =>
    `$${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;

const compactCurrency = (value) => {
    const amount = Number(value || 0);
    const abs = Math.abs(amount);

    if (abs >= 1000000) return `$${(amount / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
    if (abs >= 1000) return `$${(amount / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;

    return `$${amount.toFixed(0)}`;
};

const formatDate = (value) => (value ? TB.moment(value).format('MMM D, YYYY') : '—');
const formatMonth = (period) => (period ? TB.moment(period + '-01').format("MMM 'YY") : '');
const formatHours = (value) =>
    `${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}h`;

/* -------------------------------------------------------------------------- */
/* Health                                                                      */
/* -------------------------------------------------------------------------- */

const HEALTH = {
    healthy: {
        label: 'On Budget',
        bar: 'bg-emerald-500',
        chip: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        stroke: '#10b981',
        soft: '#d1fae5',
    },
    warning: {
        label: 'Nearing Budget',
        bar: 'bg-amber-500',
        chip: 'bg-amber-100 text-amber-800 border-amber-200',
        stroke: '#f59e0b',
        soft: '#fef3c7',
    },
    over: {
        label: 'Over Budget',
        bar: 'bg-red-500',
        chip: 'bg-red-100 text-red-800 border-red-200',
        stroke: '#ef4444',
        soft: '#fee2e2',
    },
    unknown: {
        label: 'Not Yet Billed',
        bar: 'bg-gray-300',
        chip: 'bg-gray-100 text-gray-600 border-gray-200',
        stroke: '#9ca3af',
        soft: '#f3f4f6',
    },
};

const healthOf = (project) => HEALTH[project.health] || HEALTH.unknown;

/* -------------------------------------------------------------------------- */
/* Consumed bar                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Budget consumption as a single bar. Anything past 100% is drawn as a distinct
 * overflow segment so an overrun reads as overflow rather than a full bar.
 */
const ConsumedBar = ({ project, height = 'h-2.5' }) => {
    const theme = healthOf(project);
    const consumed = project.percent_consumed || 0;
    const within = Math.min(consumed, 100);
    const over = Math.max(consumed - 100, 0);

    return (
        <div className={`relative flex ${height} rounded-full bg-gray-100 overflow-hidden`}>
            <div className={`${theme.bar} transition-all`} style={{ width: `${within}%` }} />
            {over > 0 && (
                <div
                    className="bg-red-700 transition-all"
                    style={{ width: `${Math.min(over, 40)}%` }}
                />
            )}
            {/* Reference mark at the warning threshold */}
            <div className="absolute inset-y-0 border-l border-dashed border-gray-400/60" style={{ left: '75%' }} />
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Sparkline                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Cumulative spend against the flat budget line, small enough to sit inline on
 * a project card.
 */
const BurnSparkline = ({ project, width = 160, height = 40 }) => {
    const series = project.series || [];

    if (series.length < 2) {
        return (
            <div
                className="flex items-center justify-center text-[10px] text-gray-400 bg-gray-50 rounded"
                style={{ width, height }}
            >
                No billing yet
            </div>
        );
    }

    const theme = healthOf(project);
    const max = Math.max(project.budget || 0, ...series.map(p => p.cumulative), 1);
    const stepX = width / (series.length - 1);
    const y = (value) => height - (value / max) * (height - 4) - 2;

    const line = series.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${y(p.cumulative)}`).join(' ');
    const area = `${line} L ${width} ${height} L 0 ${height} Z`;
    const budgetY = y(project.budget || 0);

    return (
        <svg width={width} height={height} className="overflow-visible">
            <path d={area} fill={theme.soft} />
            <path d={line} fill="none" stroke={theme.stroke} strokeWidth="1.75" strokeLinejoin="round" />
            {project.budget > 0 && (
                <line
                    x1="0" y1={budgetY} x2={width} y2={budgetY}
                    stroke="#6b7280" strokeWidth="1" strokeDasharray="3 3"
                />
            )}
            <circle
                cx={width} cy={y(series[series.length - 1].cumulative)} r="2.5"
                fill={theme.stroke}
            />
        </svg>
    );
};

/* -------------------------------------------------------------------------- */
/* Full burn chart                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The expanded burn up chart: monthly spend as bars, cumulative spend as a
 * filled line, and the contracted budget as a flat reference line.
 */
const BurnChart = ({ project }) => {
    const [hovered, setHovered] = useState(null);
    const series = project.series || [];

    if (!series.length) {
        return (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
                <p className="text-sm font-medium text-gray-700">No billing data for this project yet</p>
                <p className="text-xs text-gray-500 mt-1">
                    The contracted budget of {currency(project.budget)} is recorded, but no time has
                    been billed through the platform, so there is nothing to burn down against.
                </p>
            </div>
        );
    }

    const theme = healthOf(project);
    const width = 900;
    const height = 260;
    const padding = { top: 20, right: 20, bottom: 40, left: 64 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const max = Math.max(project.budget || 0, ...series.map(p => p.cumulative), 1);
    const maxMonthly = Math.max(...series.map(p => p.spend), 1);

    const band = plotWidth / series.length;
    const barWidth = Math.max(Math.min(band * 0.55, 26), 2);
    const center = (i) => padding.left + band * i + band / 2;
    const y = (value) => padding.top + plotHeight - (value / max) * plotHeight;
    const barTop = (value) => padding.top + plotHeight - (value / maxMonthly) * (plotHeight * 0.45);

    const line = series.map((p, i) => `${i === 0 ? 'M' : 'L'} ${center(i)} ${y(p.cumulative)}`).join(' ');
    const area = `${line} L ${center(series.length - 1)} ${padding.top + plotHeight} L ${center(0)} ${padding.top + plotHeight} Z`;
    const budgetY = y(project.budget || 0);

    const labelInterval = Math.ceil(series.length / 10) || 1;

    return (
        <div className="relative">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 260 }}
                 onMouseLeave={() => setHovered(null)}>
                <defs>
                    <linearGradient id={`burn-fill-${project.key}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={theme.stroke} stopOpacity="0.25" />
                        <stop offset="100%" stopColor={theme.stroke} stopOpacity="0" />
                    </linearGradient>
                </defs>

                {[0, 0.5, 1].map(ratio => {
                    const gy = padding.top + plotHeight - ratio * plotHeight;

                    return (
                        <g key={ratio}>
                            <line x1={padding.left} y1={gy} x2={width - padding.right} y2={gy}
                                  stroke="#e5e7eb" strokeWidth="1" strokeDasharray={ratio === 0 ? '0' : '4 4'} />
                            <text x={padding.left - 10} y={gy + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                                {compactCurrency(max * ratio)}
                            </text>
                        </g>
                    );
                })}

                {/* Monthly spend */}
                {series.map((point, i) => (
                    <rect
                        key={`bar-${point.period}`}
                        x={center(i) - barWidth / 2}
                        y={barTop(point.spend)}
                        width={barWidth}
                        height={Math.max(padding.top + plotHeight - barTop(point.spend), 0)}
                        fill={hovered === i ? '#94a3b8' : '#cbd5e1'}
                        rx="2"
                    />
                ))}

                {/* Cumulative spend */}
                <path d={area} fill={`url(#burn-fill-${project.key})`} />
                <path d={line} fill="none" stroke={theme.stroke} strokeWidth="2.5" strokeLinejoin="round" />

                {/* Contracted budget */}
                {project.budget > 0 && (
                    <>
                        <line x1={padding.left} y1={budgetY} x2={width - padding.right} y2={budgetY}
                              stroke="#4b5563" strokeWidth="1.5" strokeDasharray="6 4" />
                        <text x={width - padding.right} y={budgetY - 6} textAnchor="end" fontSize="11"
                              fill="#4b5563" fontWeight="600">
                            Budget {compactCurrency(project.budget)}
                        </text>
                    </>
                )}

                {series.map((point, i) => (
                    <g key={`hit-${point.period}`}>
                        <rect
                            x={padding.left + band * i} y={padding.top}
                            width={band} height={plotHeight}
                            fill="transparent"
                            onMouseEnter={() => setHovered(i)}
                        />
                        {hovered === i && (
                            <circle cx={center(i)} cy={y(point.cumulative)} r="4.5"
                                    fill={theme.stroke} stroke="#fff" strokeWidth="2" />
                        )}
                        {i % labelInterval === 0 && (
                            <text x={center(i)} y={height - padding.bottom + 20} textAnchor="middle"
                                  fontSize="11" fill="#9ca3af">
                                {formatMonth(point.period)}
                            </text>
                        )}
                    </g>
                ))}
            </svg>

            {hovered !== null && series[hovered] && (
                <div
                    className="absolute pointer-events-none z-10 bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 text-xs whitespace-nowrap"
                    style={{ left: `${(center(hovered) / width) * 100}%`, top: 4, transform: 'translateX(-50%)' }}
                >
                    <div className="font-semibold mb-1">
                        {TB.moment(series[hovered].period + '-01').format('MMMM YYYY')}
                    </div>
                    <div>Billed this month {currency(series[hovered].spend)}</div>
                    <div>Spent to date {currency(series[hovered].cumulative)}</div>
                    <div className="mt-1 pt-1 border-t border-gray-700">
                        {project.budget > 0
                            ? `${Math.round((series[hovered].cumulative / project.budget) * 100)}% of budget`
                            : 'No budget recorded'}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-center gap-6 text-xs text-gray-600 mt-2">
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-slate-300" /> Billed per month
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: theme.stroke }} /> Spent to date
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 border-t-2 border-dashed border-gray-600" /> Contracted budget
                </span>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Project detail                                                              */
/* -------------------------------------------------------------------------- */

const ProjectDetail = ({ project }) => (
    <div className="border-t border-gray-200 bg-gradient-to-br from-slate-50 to-indigo-50/30 px-5 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Burn Over Time</h4>
                <BurnChart project={project} />
            </div>

            <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                    Positions &amp; Team
                    <span className="font-normal text-gray-500"> ({project.position_count})</span>
                </h4>

                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {(project.positions || []).map((position, index) => {
                        const consumed = position.percent_consumed;

                        return (
                            <div key={`${position.title}-${index}`}
                                 className="bg-white rounded-lg border border-gray-200 p-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {position.title}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {currency(position.budget)} budget
                                            {position.bill_rate ? ` · ${currency(position.bill_rate)}/hr` : ''}
                                            {position.budget_hours > 0 ? ` · ${formatHours(position.budget_hours)}` : ''}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-semibold text-gray-900">
                                            {currency(position.spent)}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {consumed !== null && consumed !== undefined
                                                ? `${consumed}% used`
                                                : '—'}
                                        </p>
                                    </div>
                                </div>

                                {position.budget > 0 && (
                                    <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                        <div
                                            className={
                                                (consumed || 0) > 100 ? 'bg-red-500 h-full'
                                                    : (consumed || 0) >= 75 ? 'bg-amber-500 h-full'
                                                        : 'bg-emerald-500 h-full'
                                            }
                                            style={{ width: `${Math.min(consumed || 0, 100)}%` }}
                                        />
                                    </div>
                                )}

                                {position.consultants && position.consultants.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                                        {position.consultants.map((consultant, i) => (
                                            <span
                                                key={`${consultant.name}-${i}`}
                                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700"
                                            >
                                                {consultant.user && consultant.user.image_path ? (
                                                    <img src={consultant.user.image_path} alt={consultant.name}
                                                         className="w-4 h-4 rounded-full object-cover" />
                                                ) : (
                                                    <span className="w-4 h-4 rounded-full bg-gray-300" />
                                                )}
                                                {consultant.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!project.has_actuals && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
                        No time has been billed through the platform for this project, so position
                        spend reads as zero. The budget figures above still come from the signed contract.
                    </div>
                )}
            </div>
        </div>
    </div>
);

/* -------------------------------------------------------------------------- */
/* Project card                                                                */
/* -------------------------------------------------------------------------- */

const ProjectCard = ({ project, isExpanded, onToggle }) => {
    const theme = healthOf(project);
    const consumed = project.percent_consumed;
    const isOver = project.remaining < 0;

    return (
        <div className={`bg-white rounded-lg border transition-shadow ${isExpanded ? 'border-indigo-300 shadow-md' : 'border-gray-200 hover:shadow-sm'}`}>
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left px-5 py-4 focus:outline-none focus:ring-2 focus:ring-indigo-200 rounded-lg"
            >
                <div className="flex flex-wrap items-start gap-4">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">{project.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${theme.chip}`}>
                                {theme.label}
                            </span>
                            {project.is_in_flight && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                                    IN FLIGHT
                                </span>
                            )}
                            {!project.is_linked && (
                                <Tooltip title="This project is not linked to a platform record. Budget comes from contract records.">
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                        UNLINKED
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                            <span>{formatDate(project.started_at)} → {formatDate(project.ended_at)}</span>
                            <span>{project.position_count} {project.position_count === 1 ? 'position' : 'positions'}</span>
                            <span>{project.consultant_count} {project.consultant_count === 1 ? 'consultant' : 'consultants'}</span>
                            {project.project?.status && (
                                <span className="text-indigo-600">{project.project.status}</span>
                            )}
                        </div>
                    </div>

                    <div className="shrink-0">
                        <BurnSparkline project={project} />
                    </div>

                    <div className="shrink-0 text-right w-40">
                        <p className="text-xl font-bold text-gray-900 leading-tight">
                            {currency(project.spent)}
                        </p>
                        <p className="text-xs text-gray-500">
                            of {currency(project.budget)} budget
                        </p>
                        <p className={`text-xs font-medium mt-0.5 ${isOver ? 'text-red-600' : 'text-gray-600'}`}>
                            {isOver
                                ? `${currency(Math.abs(project.remaining))} over`
                                : `${currency(project.remaining)} remaining`}
                        </p>
                    </div>

                    <svg
                        className={`w-5 h-5 text-gray-400 shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1">
                        <ConsumedBar project={project} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 w-14 text-right">
                        {consumed !== null && consumed !== undefined ? `${consumed}%` : '—'}
                    </span>
                </div>
            </button>

            {isExpanded && <ProjectDetail project={project} />}
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Summary card                                                                */
/* -------------------------------------------------------------------------- */

const SummaryCard = ({ label, value, sub, icon, theme, loading }) => {
    const themes = {
        indigo: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-600 text-indigo-900 bg-indigo-200 text-indigo-700',
        emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-600 text-emerald-900 bg-emerald-200 text-emerald-700',
        amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-600 text-amber-900 bg-amber-200 text-amber-700',
        slate: 'from-slate-50 to-slate-100 border-slate-200 text-slate-600 text-slate-900 bg-slate-200 text-slate-700',
    };

    const [gradFrom, gradTo, border, labelColor, valueColor, iconBg, iconColor] =
        (themes[theme] || themes.indigo).split(' ');

    return (
        <div className={`bg-gradient-to-br ${gradFrom} ${gradTo} rounded-lg p-5 border ${border} relative`}>
            {loading && (
                <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-gray-500" />
                </div>
            )}
            <div className="flex items-start justify-between">
                <div className="min-w-0">
                    <p className={`text-sm font-medium ${labelColor} mb-1`}>{label}</p>
                    <p className={`text-3xl font-bold ${valueColor} leading-tight`}>{value}</p>
                    {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
                </div>
                <div className={`${iconBg} rounded-full p-3 shrink-0 ml-3`}>
                    <svg className={`w-6 h-6 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {icon}
                    </svg>
                </div>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Main view                                                                   */
/* -------------------------------------------------------------------------- */

const ProjectBurnSpend = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandedKeys, setExpandedKeys] = useState([]);

    const decodedSearchParams = useMemo(() => {
        return [...searchParams.entries()].reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
    }, [searchParams.toString()]);

    // Freshness is configured in QueryOptions: refetched on every mount with
    // nothing retained between mounts or client sessions.
    const { data, isFetching, error } = useAPI(['fetchProjectBurnSpend']);

    const projects = data?.projects || [];
    const totals = data?.totals || {};

    const scope = decodedSearchParams.scope || 'active';

    /* ---------------------------------------------------------------------- */
    /* Filtering + sorting                                                     */
    /* ---------------------------------------------------------------------- */

    const filteredProjects = useMemo(() => {
        let rows = [...projects];

        if (scope === 'active') {
            rows = rows.filter(project => project.is_in_flight);
        } else if (scope === 'completed') {
            rows = rows.filter(project => !project.is_in_flight);
        }

        const search = decodedSearchParams.search?.toLowerCase();
        if (search) {
            rows = rows.filter(project =>
                (project.name || '').toLowerCase().includes(search)
                || (project.project?.name || '').toLowerCase().includes(search)
                || (project.positions || []).some(position =>
                    (position.title || '').toLowerCase().includes(search)
                    || (position.consultants || []).some(consultant =>
                        (consultant.name || '').toLowerCase().includes(search))
                )
            );
        }

        const health = decodedSearchParams.health;
        if (health && health !== 'all') {
            rows = rows.filter(project => project.health === health);
        }

        const sortBy = decodedSearchParams.sort_by || 'budget';
        const direction = decodedSearchParams.sort_dir === 'asc' ? 1 : -1;

        const value = (project) => {
            switch (sortBy) {
                case 'spent': return project.spent || 0;
                case 'consumed': return project.percent_consumed || 0;
                case 'remaining': return project.remaining || 0;
                case 'name': return project.name || '';
                default: return project.budget || 0;
            }
        };

        return rows.sort((a, b) => {
            const av = value(a);
            const bv = value(b);

            if (typeof av === 'string') return av.localeCompare(bv) * direction;

            return (av - bv) * direction;
        });
    }, [projects, scope, decodedSearchParams.search, decodedSearchParams.health,
        decodedSearchParams.sort_by, decodedSearchParams.sort_dir]);

    // Totals track what is on screen so the cards and the list always agree.
    const visibleTotals = useMemo(() => {
        if (filteredProjects.length === projects.length) {
            return totals;
        }

        const budget = filteredProjects.reduce((sum, p) => sum + (p.budget || 0), 0);
        const spent = filteredProjects.reduce((sum, p) => sum + (p.spent || 0), 0);

        return {
            budget,
            spent,
            remaining: budget - spent,
            percent_consumed: budget > 0 ? Math.round((spent / budget) * 1000) / 10 : null,
            actual_hours: filteredProjects.reduce((sum, p) => sum + (p.actual_hours || 0), 0),
            project_count: filteredProjects.length,
            in_flight_count: filteredProjects.filter(p => p.is_in_flight).length,
            untracked_count: filteredProjects.filter(p => !p.has_actuals).length,
            over_count: filteredProjects.filter(p => p.health === 'over').length,
            warning_count: filteredProjects.filter(p => p.health === 'warning').length,
            healthy_count: filteredProjects.filter(p => p.health === 'healthy').length,
        };
    }, [filteredProjects, projects, totals]);

    /* ---------------------------------------------------------------------- */
    /* Handlers                                                                */
    /* ---------------------------------------------------------------------- */

    const updateParams = (mutate) => {
        const newParams = new URLSearchParams(searchParams);
        mutate(newParams);
        setSearchParams(newParams);
    };

    const handleSearch = (value) => updateParams(params => {
        value ? params.set('search', value) : params.delete('search');
    });

    const handleScopeChange = (value) => updateParams(params => {
        value === 'active' ? params.delete('scope') : params.set('scope', value);
    });

    const handleHealthChange = (value) => updateParams(params => {
        value && value !== 'all' ? params.set('health', value) : params.delete('health');
    });

    const handleSortChange = (value) => updateParams(params => {
        params.set('sort_by', value);
    });

    const toggleExpanded = (key) => setExpandedKeys(keys =>
        keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]
    );

    const clearFilters = () => setSearchParams(new URLSearchParams());

    /* ---------------------------------------------------------------------- */
    /* States                                                                  */
    /* ---------------------------------------------------------------------- */

    if (isFetching && !data) {
        return (
            <div className="project-burn-spend">
                <div className="p-6 border-b border-gray-200">
                    <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-6" />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map(card => (
                            <div key={card} className="bg-gray-100 rounded-lg p-5 border border-gray-200 animate-pulse">
                                <div className="h-4 w-24 bg-gray-300 rounded mb-3" />
                                <div className="h-8 w-32 bg-gray-300 rounded" />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="p-6 space-y-3">
                    {[1, 2, 3, 4, 5].map(row => (
                        <div key={row} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="project-burn-spend">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Project Burn &amp; Spend</h2>
                </div>
                <div className="p-6">
                    <div className="rounded-lg border-2 border-red-200 bg-red-50 p-12">
                        <div className="flex flex-col items-center justify-center text-center">
                            <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                We couldn't load your burn report
                            </h3>
                            <p className="text-gray-600 mb-6 max-w-md">
                                {error?.message || 'An unexpected error occurred while loading project data. Please try again.'}
                            </p>
                            <button onClick={clearFilters}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium">
                                Reset &amp; Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const hasProjects = projects.length > 0;
    const isEmpty = filteredProjects.length === 0;
    const overallConsumed = visibleTotals.percent_consumed;

    /* ---------------------------------------------------------------------- */
    /* Render                                                                  */
    /* ---------------------------------------------------------------------- */

    return (
        <div className="project-burn-spend">

            {/* Header + headline figures */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Project Burn &amp; Spend</h2>
                        <p className="text-gray-600 text-sm mt-1">
                            Contracted budget against what has actually been billed, month by month.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {[
                            { key: 'healthy', count: visibleTotals.healthy_count },
                            { key: 'warning', count: visibleTotals.warning_count },
                            { key: 'over', count: visibleTotals.over_count },
                        ].map(({ key, count }) => (
                            <span key={key}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${HEALTH[key].chip}`}>
                                {count || 0} {HEALTH[key].label}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    <SummaryCard
                        theme="indigo"
                        loading={isFetching}
                        label="Contracted Budget"
                        value={currency(visibleTotals.budget)}
                        sub={`${visibleTotals.project_count || 0} projects`}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
                    />
                    <SummaryCard
                        theme="emerald"
                        loading={isFetching}
                        label="Actually Billed"
                        value={currency(visibleTotals.spent)}
                        sub={overallConsumed !== null && overallConsumed !== undefined
                            ? `${overallConsumed}% of budget consumed`
                            : 'No billing recorded'}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                    />
                    <SummaryCard
                        theme={visibleTotals.remaining < 0 ? 'amber' : 'slate'}
                        loading={isFetching}
                        label={visibleTotals.remaining < 0 ? 'Over Budget By' : 'Budget Remaining'}
                        value={currency(Math.abs(visibleTotals.remaining || 0))}
                        sub={`${visibleTotals.in_flight_count || 0} projects in flight`}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />}
                    />
                    <SummaryCard
                        theme="slate"
                        loading={isFetching}
                        label="Hours Billed"
                        value={formatHours(visibleTotals.actual_hours)}
                        sub={visibleTotals.untracked_count > 0
                            ? `${visibleTotals.untracked_count} projects not yet billed`
                            : 'All projects have billing'}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />}
                    />
                </div>

                {/* Overall consumption */}
                {visibleTotals.budget > 0 && (
                    <div className="mt-6">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                            <span className="font-medium">Overall budget consumed</span>
                            <span>
                                {currency(visibleTotals.spent)} of {currency(visibleTotals.budget)}
                                {overallConsumed !== null ? ` · ${overallConsumed}%` : ''}
                            </span>
                        </div>
                        <ConsumedBar
                            project={{
                                percent_consumed: overallConsumed,
                                health: overallConsumed > 100 ? 'over' : overallConsumed >= 75 ? 'warning' : 'healthy',
                            }}
                            height="h-3"
                        />
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 transition-opacity ${isFetching ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[220px]">
                        <Search
                            placeholder="Search projects, positions, or consultants..."
                            allowClear
                            onSearch={handleSearch}
                            defaultValue={decodedSearchParams.search}
                            className="w-full"
                            disabled={isFetching}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap font-medium">Show:</span>
                        <Select value={scope} onChange={handleScopeChange} style={{ width: 170 }} disabled={isFetching}>
                            <Option value="active">In flight today</Option>
                            <Option value="all">All projects</Option>
                            <Option value="completed">Outside date range</Option>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">Health:</span>
                        <Select value={decodedSearchParams.health || 'all'} onChange={handleHealthChange}
                                style={{ width: 170 }} disabled={isFetching}>
                            <Option value="all">All</Option>
                            <Option value="healthy">On budget</Option>
                            <Option value="warning">Nearing budget</Option>
                            <Option value="over">Over budget</Option>
                            <Option value="unknown">Not yet billed</Option>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">Sort:</span>
                        <Select value={decodedSearchParams.sort_by || 'budget'} onChange={handleSortChange}
                                style={{ width: 170 }} disabled={isFetching}>
                            <Option value="budget">Largest budget</Option>
                            <Option value="spent">Most billed</Option>
                            <Option value="consumed">% consumed</Option>
                            <Option value="remaining">Least remaining</Option>
                            <Option value="name">Project name</Option>
                        </Select>
                    </div>
                </div>

                {(decodedSearchParams.search || decodedSearchParams.health || decodedSearchParams.scope) && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className="text-xs text-gray-600 font-medium">Active filters:</span>
                        {decodedSearchParams.search && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md border border-blue-200">
                                Search: "{decodedSearchParams.search}"
                            </span>
                        )}
                        {decodedSearchParams.scope && (
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md border border-indigo-200">
                                {decodedSearchParams.scope === 'all' ? 'All projects' : 'Outside date range'}
                            </span>
                        )}
                        {decodedSearchParams.health && (
                            <span className={`px-2 py-1 text-xs rounded-md border ${HEALTH[decodedSearchParams.health]?.chip || ''}`}>
                                {HEALTH[decodedSearchParams.health]?.label}
                            </span>
                        )}
                        <button onClick={clearFilters} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Projects */}
            <div className="p-6">
                {isFetching && (
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        <span className="text-sm text-blue-800 font-medium">Loading burn data...</span>
                    </div>
                )}

                {isEmpty ? (
                    <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12">
                        <div className="flex flex-col items-center justify-center text-center">
                            <svg className="w-20 h-20 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {hasProjects
                                    ? (scope === 'active' ? 'No projects are in flight right now' : 'No projects match your filters')
                                    : 'No project budgets to show yet'}
                            </h3>
                            <p className="text-gray-600 mb-6 max-w-md">
                                {hasProjects
                                    ? 'Switch to all projects to see budgets and spend across your full history.'
                                    : 'Once projects are contracted for your organization, their budget and burn will appear here.'}
                            </p>
                            {hasProjects && (
                                <button
                                    onClick={() => updateParams(params => {
                                        params.delete('search');
                                        params.delete('health');
                                        params.set('scope', 'all');
                                    })}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                                >
                                    Show All Projects
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <p className="text-sm text-gray-600">
                                Showing <span className="font-semibold text-gray-900">{filteredProjects.length}</span>
                                {filteredProjects.length !== projects.length && (
                                    <> of <span className="font-semibold text-gray-900">{projects.length}</span></>
                                )}
                                {' '}projects
                            </p>
                            <button
                                onClick={() => setExpandedKeys(
                                    expandedKeys.length ? [] : filteredProjects.map(p => p.key)
                                )}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                                {expandedKeys.length ? 'Collapse all' : 'Expand all'}
                            </button>
                        </div>

                        <div className="space-y-3">
                            {filteredProjects.map(project => (
                                <ProjectCard
                                    key={project.key}
                                    project={project}
                                    isExpanded={expandedKeys.includes(project.key)}
                                    onToggle={() => toggleExpanded(project.key)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProjectBurnSpend;
